// App.tsx
import React from 'react';
import { ArrowLeft, Github, ExternalLink, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Project1 from "@/assets/achivements/Project1.jpg";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Circuitdiagram from "@/assets/achivements/Circuitdiagram.png"; 
import WorkingDual from "@/assets/achivements/WorkingDual.png";

const Dual_Axis_Solar_Tracker: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Dual Axis Solar Tracker</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">Arduino</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Servo Motors</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">LDR Sensors</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Solar Panel</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img src={Project1} alt="Dual Axis Solar Tracker" className="w-full object-cover" />
              </div>
              <div>
                <img src={Circuitdiagram} alt="Circuit Diagram" className="w-full object-cover" />
              </div>
              <div>
                <img src={WorkingDual} alt="LDR Sensor Array" className="w-full object-cover" />
              </div>
              
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  A system that optimizes solar energy collection by adjusting solar panels in two axes to follow the sun.
                  The Dual Axis Solar Tracker is designed to maximize the efficiency of solar panels by ensuring they 
                  remain perpendicular to the sun's rays throughout the day.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">How It Works</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The system uses light-dependent resistors (LDRs) to detect the position of the sun. Four LDRs are 
                  arranged in a specific pattern and separated by barriers. The Arduino microcontroller continuously 
                  reads values from these sensors and calculates the optimal position for the solar panel. Two servo 
                  motors adjust the panel's position in both horizontal and vertical axes, ensuring it faces the sun 
                  throughout the day.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Real-time sun tracking in two axes (horizontal and vertical)</li>
                  <li>Light sensor array for accurate sun position detection</li>
                  <li>Energy-efficient servo motor control system</li>
                  <li>Microcontroller-based autonomous operation</li>
                  <li>Significantly improved solar energy collection compared to fixed panels</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The project uses an Arduino UNO as the main controller, connected to four LDR sensors arranged in 
                  quadrants. The sensors continuously monitor light intensity, and the Arduino calculates the 
                  difference between readings. Based on these calculations, it controls two servo motors that adjust 
                  the panel's position. The system includes power management to ensure efficient operation and 
                  protection against environmental conditions.
                </p>
              </div>
              
              {/* Video demo */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Demo</h2>
                <div className="aspect-video w-full overflow-hidden rounded-lg">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/GyG84TlI-rc" 
                    title="Dual Axis Solar Tracker" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    className="aspect-video"
                  ></iframe>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/Dual_Axis_Solar_Tracker" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://youtu.be/GyG84TlI-rc?si=CC0333TKLqY8AniB" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Youtube className="mr-2 h-5 w-5" />
                      <span>Video Demo</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Arduino</p>
                      <p className="text-sm text-muted-foreground">Microcontroller platform</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">S</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Servo Motors</p>
                      <p className="text-sm text-muted-foreground">Precision positioning</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">L</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">LDR Sensors</p>
                      <p className="text-sm text-muted-foreground">Light detection</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">S</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Solar Panel</p>
                      <p className="text-sm text-muted-foreground">Energy collection</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Outcome</h3>
                <p className="text-muted-foreground mb-4">
                  The dual-axis solar tracker demonstrated a significant improvement in energy collection efficiency 
                  compared to fixed solar panel installations.
                </p>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Efficiency Gain</span>
                  <span className="text-primary font-medium">+30-45%</span>
                </div>
                <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full w-[40%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Automated_Public_Lighting_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Automated Public Lighting System</h3>
              <p className="text-muted-foreground line-clamp-2">An advanced lighting system designed to optimize energy capture and utilization.</p>
            </Link>
            
            <Link to="/Smart_Irrigation_System_using_Precision_Farming" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Smart Irrigation System</h3>
              <p className="text-muted-foreground line-clamp-2">A system that optimizes water usage in agriculture by monitoring soil moisture levels.</p>
            </Link>
            
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Dual_Axis_Solar_Tracker;
